
import React, {useEffect, useRef, useState} from 'react';
import resources from './resources/jegerregisteret_logo.png';
import './jegeravgiftkort.scss';
import {JegerwebApiService} from '../../../services/api-service/jegerweb-api-service';
import Tablevaapen from '../vaapentable/tablevaapen';
import {AuthGuard} from '../../../utilities/session-management/auth-guard';
import {SessionType} from '../../../utilities/session-management/session-manager';
import JegerKontrollQRcode from './jegerKontrollQRcode';
import i18n from '../../../utilities/i18n';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPrint} from '@fortawesome/free-solid-svg-icons';

const oversettStatus = (statusVerdi) => {
  const oversettelser = {
    'Egen ettersøkshund': 'egenEttersokshund',
  };

  const oversettelsesnokkel = oversettelser[statusVerdi];

  return oversettelsesnokkel
      ? i18n.t(`minJegerside:${oversettelsesnokkel}`)
      : statusVerdi;
};

const oversettLisensArter = (arter) => {
  if (!arter) {
    return arter;
  }

  const oversettelser = {
    Jerv: 'jerv',
    Gaupe: 'gaupe',
    Bjørn: 'bjorn',
    Ulv: 'ulv',
  };

  return arter
      .split(',')
      .map((art) => {
        const verdi = art.trim();
        const nokkel = oversettelser[verdi];

        return nokkel
            ? i18n.t(`minJegerside:${nokkel}`)
            : verdi;
      })
      .join(', ');
};

const Jegeravgiftkort = (props) => {
  const [jegerWebData, setJegerWebData] = useState({});
  const [defaultErrorMessage, setDefaultErrorMessage] = useState('');
  const [status, setStatus] = useState([]);
  const [loading, setLoading] = useState(true);

  const kortRef = useRef(null);

  useEffect(() => {
    const getJegerweb = async () => {
      try {
        const response = await JegerwebApiService.get(
            '/jeger/:sessiontype/:lang',
        );

        setJegerWebData(response.data);
        setDefaultErrorMessage('');
      } catch (err) {
        setDefaultErrorMessage(
            i18n.t('minJegerside:defaultErrorMessage'),
        );

        console.error('Kunne ikke hente jegeropplysninger', err);
      } finally {
        setLoading(false);
      }
    };

    const getStatus = async () => {
      try {
        const response = await JegerwebApiService.get(
            '/jeger/:sessiontype/status/',
        );

        setStatus(response.data);
      } catch (err) {
        console.error('Kunne ikke hente jegerstatus', err);
      }
    };

    getJegerweb();
    getStatus();
  }, []);

  const minstEttGodkjentVaapen = (vaapens = []) =>
      vaapens.some((vaapen) => vaapen.godkjentDato);

  const skrivUtJegeravgiftskort = async () => {
    if (!kortRef.current) {
      return;
    }

    const printWindow = window.open(
        '',
        '_blank',
        'width=1000,height=800',
    );

    if (!printWindow) {
      window.alert(
          'Utskriftsvinduet ble blokkert. Tillat popup-vinduer og prøv igjen.',
      );
      return;
    }

    const kortKopi = kortRef.current.cloneNode(true);

    kortKopi.querySelectorAll('.no-print').forEach((element) => {
      element.remove();
    });

    // Kopier QR-koden som bilde hvis den er rendret som canvas.
    const originaleCanvas = kortRef.current.querySelectorAll(
        '.qrcode canvas',
    );
    const kopierteCanvas = kortKopi.querySelectorAll(
        '.qrcode canvas',
    );

    originaleCanvas.forEach((canvas, index) => {
      const kopiCanvas = kopierteCanvas[index];

      if (!kopiCanvas) {
        return;
      }

      try {
        const img = document.createElement('img');

        img.src = canvas.toDataURL('image/png');
        img.alt = 'QR-kode';
        img.width = canvas.width;
        img.height = canvas.height;
        img.style.display = 'block';
        img.style.width = '125px';
        img.style.height = '125px';
        img.style.maxWidth = '100%';
        img.style.margin = '0 auto';

        kopiCanvas.replaceWith(img);
      } catch (err) {
        console.error('Kunne ikke kopiere QR-koden', err);
      }
    });

    const stylesheetLinks = Array.from(
        document.querySelectorAll('link[rel="stylesheet"]'),
    )
        .map((link) => {
          const href = link.href
              .replace(/&/g, '&amp;')
              .replace(/"/g, '&quot;');

          return `<link rel="stylesheet" href="${href}">`;
        })
        .join('\n');

    const printStyles = `
      @page {
        size: A4 portrait;
        margin: 10mm;
      }

      html,
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
      }

      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .print-card {
        width: 100%;
        margin: 0;
        padding: 0;
      }

      .print-card .betalt-wrapper {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        box-sizing: border-box !important;
        box-shadow: none !important;
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .print-card .no-print {
        display: none !important;
      }

      .print-card .jegerbilde {
        max-width: 230px !important;
        height: auto !important;
      }

      .print-card .qrcode {
        width: 200px !important;
        margin: auto !important;
        text-align: center !important;
      }

      .print-card .qrcode canvas,
      .print-card .qrcode img {
        display: block !important;
        width: 125px !important;
        height: 125px !important;
        max-width: 100% !important;
        margin: 0 auto !important;
        object-fit: contain !important;
      }

      .print-card .kort-content,
      .print-card .jegeravgiftskort-vaapen-table-wrapper,
      .print-card table {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .print-card .betalt-titlebar {
        background-color: #105522 !important;
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }

      .print-card .betalt-tekst {
        color: #105522 !important;
      }
    `;

    const currentLanguage = i18n.language || 'nb';
    const title = i18n.t('minJegerside:jegeravgiftskort');

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="${currentLanguage}">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <title>${title}</title>
          ${stylesheetLinks}
          <style>${printStyles}</style>
        </head>
        <body>
          <main class="print-card">
            ${kortKopi.outerHTML}
          </main>
        </body>
      </html>
    `);
    printWindow.document.close();

    try {
      await new Promise((resolve) => {
        if (printWindow.document.readyState === 'complete') {
          resolve();
          return;
        }

        printWindow.addEventListener('load', resolve, {once: true});
      });

      await Promise.all(
          Array.from(printWindow.document.images).map((img) => {
            if (img.complete) {
              return Promise.resolve();
            }

            return new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            });
          }),
      );

      if (printWindow.document.fonts?.ready) {
        await printWindow.document.fonts.ready;
      }

      printWindow.focus();
      printWindow.print();
    } catch (err) {
      console.error('Kunne ikke klargjøre utskrift', err);
    }
  };

  if (loading) {
    return null;
  }

  return (
      <section title="jegeravgiftskort">
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-10 col-lg-8">

            <div className="jegeravgiftskort-actions no-print print-jegeravgiftskort">
              <button type="button"
                      className="btn btn-simple"
                      onClick={skrivUtJegeravgiftskort}
              >
                <FontAwesomeIcon icon={faPrint} />
                {' '}
                {i18n.t('minJegerside:skrivUtJegeravgiftskort')}
              </button>
            </div>

            <div ref={kortRef}
                 className="betalt-wrapper notranslate"
            >
              {defaultErrorMessage && (
                  <h1 className="nettverksfeil">
                    {defaultErrorMessage}
                  </h1>
              )}

              <header>
                <div className="betalt-titlebar">
                  <h2>
                    {i18n.t('minJegerside:jegeravgiftskort')}
                  </h2>
                </div>
              </header>

              <div>
                <br />

                <div className="state-logo">
                  <figure>
                    <img className="jegerbilde"
                         src={resources}
                         alt="Jegerregisteret"
                    />
                  </figure>
                </div>

                <div className="kort-content">
                  <div>
                    <table className="jegeravgiftskort-table">
                      <tbody>
                      <tr>
                        <td className="state">
                          {i18n.t('minJegerside:navn')}
                          :
                        </td>
                        <td>
                          <strong>
                            {jegerWebData.jeger?.navn}
                          </strong>
                        </td>
                      </tr>

                      <tr>
                        <td className="state">
                          {i18n.t('minJegerside:fodselsaar')}
                          :
                        </td>
                        <td>
                          <strong>
                            {jegerWebData.jeger?.fodselsaar}
                          </strong>
                        </td>
                      </tr>

                      <tr>
                        <td className="state">
                          {i18n.t('minJegerside:jegernummer')}
                          :
                        </td>
                        <td>
                          <strong>{jegerWebData.jegernr}</strong>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <strong className="betalt-tekst state">
                            {i18n.t('minJegerside:betalt')}
                          </strong>
                        </td>
                        <td>
                          <strong className="betalt-tekst">
                            {jegerWebData.jaktaar}
                          </strong>
                        </td>
                      </tr>

                      <tr>
                        <td className="state">
                          {i18n.t('minJegerside:jakttype')}
                          :
                        </td>
                        <td>
                          <strong>{jegerWebData.typejakt}</strong>
                        </td>
                      </tr>

                      {status.length > 0 && (
                          <tr>
                            <td className="state">
                              {i18n.t('minJegerside:ettersokshund')}
                              :
                            </td>
                            <td>
                              <strong>
                                {Array.isArray(status)
                                    ? status.map(oversettStatus).join(', ')
                                    : oversettStatus(status)}
                              </strong>
                            </td>
                          </tr>
                      )}

                      {jegerWebData.lisensArter && (
                          <tr>
                            <td className="state">
                              {i18n.t('minJegerside:lisens')}
                              {' '}
                              {jegerWebData.lisensJaktaar}
                              :
                            </td>
                            <td>
                              <strong>
                                {oversettLisensArter(jegerWebData.lisensArter)}
                              </strong>
                            </td>
                          </tr>
                      )}
                      </tbody>
                    </table>
                  </div>

                  <JegerKontrollQRcode />

                  <AuthGuard sessionType={SessionType.OIDC}>
                    {minstEttGodkjentVaapen(props.vaapens || []) && (
                        <div className="jegeravgiftskort-vaapen-table-wrapper">
                          <h3>
                            <strong>
                              {i18n.t('minJegerside:skyteproveavlagtmed')}
                            </strong>
                          </h3>

                          <Tablevaapen vaapenData={(props.vaapens || []).filter(
                              (vaapen) => vaapen.godkjentDato,
                              )} hideDeleteColumns
                          />
                        </div>
                    )}
                  </AuthGuard>

                  {jegerWebData.infotekst && (
                      <div>{jegerWebData.infotekst}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
};

export default Jegeravgiftkort;
